import { useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import CategoryNav from "./CategoryNav";
import CategoryHero from "./CategoryHero";
import { ShoppingCard } from "./Shopping";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import { SOCIAL_FIELDS } from './SocialLinks';

const SALON_SPA_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "address", label: "Address", type: "textarea" },
  { key: "location", label: "Location", placeholder: "Frisco" },
  { key: "description", label: "Type", placeholder: "Salon / Med spa / Beauty salon" },
  { key: "shopUrl", label: "Website / Booking URL", type: "url" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

const SALON_IMAGE = "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-21/4496207897c7.jpeg";
const BEAUTY_SALON_IMAGE = "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-21/3fac1bef5e5c.jpeg";
const MED_SPA_IMAGE = "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-21/ae5b6d5359e8.jpeg";

const HERO_LOCATIONS = ["All Locations", "Dallas", "Frisco", "Plano", "Irving", "Carrollton", "McKinney", "Allen", "Flower Mound", "Mansfield", "Aubrey", "Other"];

export const SALON_SPA_STORES = [
  {
    id: "ambreens-salon",
    name: "Ambreens Salon",
    address: "12455 Eldorado Pkwy suite 260, Frisco, TX 75035",
    location: "Frisco",
    image: SALON_IMAGE,
    shopUrl: "",
    description: "Salon",
  },
  {
    id: "ashimas-salon",
    name: "Ashima's Salon",
    address: "700 W Spring Creek Pkwy Ste 108A, Plano, Texas 75023",
    location: "Plano",
    image: SALON_IMAGE,
    shopUrl: "",
    description: "Salon",
  },
  {
    id: "hair-haus",
    name: "HAIR HAUS",
    address: "2840 FM157 Ste 122, Mansfield, TX 76063",
    location: "Mansfield",
    image: BEAUTY_SALON_IMAGE,
    shopUrl: "",
    description: "Salon",
  },
  {
    id: "skin-hub-med-spa",
    name: "SKIN HUB Med Spa",
    address: "2727 Main St Ste 650, Frisco, TX 75036",
    location: "Frisco",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1792781109907-iz1acc.png",
    shopUrl: "",
    description: "Med spa",
    instagram: "https://www.instagram.com/p/DezeQR5MMm6/",
  },
  {
    id: "sanjiva-med-spa",
    name: "Sanjiva Med Spa",
    address: "5633 W Lovers Ln, Dallas, TX 75209",
    location: "Dallas",
    image: MED_SPA_IMAGE,
    shopUrl: "",
    description: "Med spa",
    instagram: "https://www.instagram.com/p/DS_yseQjSmC/",
  },
  {
    id: "siya-beauty-salon",
    name: "SIYA Beauty Salon",
    address: "3634 Long Prairie Rd #132, Flower Mound, TX 75022",
    location: "Flower Mound",
    image: BEAUTY_SALON_IMAGE,
    shopUrl: "",
    description: "Beauty salon",
  },
  {
    id: "vio-med-spa",
    name: "VIO Med Spa",
    address: "9440 Garland Rd, Dallas, TX 75218",
    location: "Dallas",
    image: MED_SPA_IMAGE,
    shopUrl: "",
    description: "Med spa",
  },
  {
    id: "elegant-beauty-services",
    name: "Elegant Beauty services",
    address: "",
    location: "Dallas",
    image: BEAUTY_SALON_IMAGE,
    shopUrl: "https://www.facebook.com/groups/467166136634442",
    description: "Beauty salon",
  },
  {
    id: "zelene-head-spa",
    name: "Zelene Head Spa",
    address: "Multiple locations in DFW",
    location: "Dallas",
    image: MED_SPA_IMAGE,
    shopUrl: "",
    description: "Head spa",
    website: "https://zelenespa.com/",
    instagram: "https://www.instagram.com/zeleneheadspa",
  },
];

function mapsUrl(name: string, address: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${address}`)}`;
}

// Cards featured by TastebudsByAnubhi — matched by name so it survives
// managed-collection re-saves the same way Restaurants/Desserts/Grocery/Kids do.
const FEATURED_SALON_NAMES = new Set(["sanjiva med spa", "skin hub med spa"]);
function isFeaturedSalon(s: { name?: string }) {
  return FEATURED_SALON_NAMES.has(String(s?.name || "").trim().toLowerCase());
}

export default function SalonSpa() {
  const [query, setQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const mc = useManagedCollection<any>("salon_spa", SALON_SPA_STORES, (s, i) => slugify(s.id || s.name || String(i)));

  const activeItems = mc.items;

  const filteredStores = activeItems.filter((store) => {
    const matchesQuery =
      query.trim() === "" ||
      store.name.toLowerCase().includes(query.toLowerCase()) ||
      (store.description || "").toLowerCase().includes(query.toLowerCase());
    const matchesLocation = !selectedLocation || store.location === selectedLocation;
    return matchesQuery && matchesLocation;
  });

  const featuredStores = filteredStores.filter(isFeaturedSalon);
  const nonFeaturedStores = filteredStores.filter((s) => !isFeaturedSalon(s));

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Salon & Spa"
        placeholder="Search salons & spas…"
        query={query}
        setQuery={setQuery}
        location={selectedLocation ?? "All Locations"}
        setLocation={(loc) => setSelectedLocation(loc === "All Locations" ? null : loc)}
        locations={HERO_LOCATIONS}
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-25/9187797c5972.png"
      />
      <NewsTicker />

      <CategoryNav />

      {/* Listings */}
      <section className="bg-gray-50/50 py-8">
        <div className="max-w-[1320px] mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
          <div className="mb-6">
            <h2
              className="text-[18px] sm:text-[20px] md:text-[22px] font-bold text-gray-900 mb-2"
             
            >
              Discover Salons &amp; Spas in Dallas–Fort Worth
            </h2>
            <p
              className="text-[13px] sm:text-[14px] text-gray-600 mb-3"
             
            >
              Looking for the best salons and spas in Dallas–Fort Worth (DFW)? Dallas Desi Scoop helps you discover local beauty and wellness businesses, including hair salons, beauty parlors, nail salons, spas, skincare clinics, makeup artists, and massage services serving the DFW community.
            </p>
            <p
              className="text-[13px] sm:text-[14px] text-gray-600 mb-3"
             
            >
              Whether you're searching for a haircut in Frisco, a beauty salon in Plano, a relaxing spa in Irving, or professional hair and skincare services in Dallas, explore local businesses in one convenient place. Discover salons and spas across Frisco, Plano, Irving, McKinney, Carrollton, Richardson, Allen, and surrounding DFW cities with Dallas Desi Scoop.
            </p>
          </div>

            <AdminAddBar mc={mc} fields={SALON_SPA_FIELDS} label="Add Salon / Spa" />

            {/* Featured by TastebudsByAnubhi */}
            {featuredStores.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[14px] sm:text-[16px] font-bold text-green-700 mb-3 flex items-center gap-2">
                  Featured by TastebudsByAnubhi
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5">
                  {featuredStores.map((store, index) => (
                    <AdminItem key={store._key} mc={mc} item={store} fields={SALON_SPA_FIELDS} title="Salon / Spa">
                      <ShoppingCard store={store as any} index={index} compact forceFeatured linkDetail detailBase="/salon-spa" />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {filteredStores.length > 0 ? (
              <div>
                {featuredStores.length > 0 && (
                  <h3 className="text-[14px] sm:text-[16px] font-bold text-gray-700 mb-3">
                    More Salons & Spas
                  </h3>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5">
                  {nonFeaturedStores.map((store, index) => (
                    <AdminItem key={store._key} mc={mc} item={store} fields={SALON_SPA_FIELDS} title="Salon / Spa">
                      <ShoppingCard store={store as any} index={index} compact linkDetail detailBase="/salon-spa" />
                    </AdminItem>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-400 text-[14px]">No salons or spas found for this search.</p>
              </div>
            )}

            {/* SEO text block */}
            <div className="mt-10 pt-8 border-t border-gray-200">
              <h2 className="text-[18px] sm:text-[20px] md:text-[22px] font-bold text-gray-900 mb-3">
                Find the Right Beauty &amp; Wellness Services Near You
              </h2>
              <p className="text-[13px] sm:text-[14px] text-gray-600 mb-3">
                From everyday grooming and hairstyling to special-occasion makeup, facials, manicures, pedicures, and relaxing spa treatments, Dallas–Fort Worth offers a wide range of beauty and wellness services to suit your needs. Explore Indian salons, Desi beauty parlors, hair and nail salons, day spas, and skincare professionals near you to find services that match your style and preferences.
              </p>
              <p className="text-[13px] sm:text-[14px] text-gray-600">
                Dallas Desi Scoop makes it easier to discover salons and spas in Frisco, Plano, Dallas, Irving, and across DFW. Browse our local business directory to explore beauty and wellness providers, discover new places, and support businesses serving the local community. Whether you need a regular salon visit or beauty services for a wedding or special event, start your search with Dallas Desi Scoop.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
